import { Link } from "react-router-dom";
import { Check, Sparkles, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import SectionHeading from "@/components/SectionHeading";

const tiers = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    tagline: "Set up your first vault. Built-in for life.",
    features: [
      "1 vault with up to 25 items",
      "AES-256-GCM encryption",
      "Password generator",
      "Auto-lock & clipboard clear",
      "Local-only storage",
      "Cross-platform desktop & mobile",
    ],
    cta: "Download free",
    accent: false,
  },
  {
    name: "Pro",
    price: "$3.99",
    period: "/ month, billed yearly",
    tagline: "Everything. Unlimited vaults. Forever-private.",
    features: [
      "Unlimited vaults & items",
      "Decoy vault & duress mode",
      "Biometric unlock & screenshot protection",
      "Encrypted backup & restore",
      "Optional k-anonymous breach check",
      "Priority email support",
    ],
    cta: "Upgrade to Pro",
    accent: true,
  },
];

const faqs = [
  { q: "Is the free tier actually free?", a: "Yes. No trial. No credit card. Set up your first vault and use it forever." },
  { q: "When do I need Pro?", a: "When you want a second vault, a decoy vault, biometric unlock, or encrypted cloud-portable backups." },
  { q: "Can I switch back to free?", a: "Always. Your existing items remain encrypted and accessible — Pro features simply unlist." },
  { q: "Family / team plans?", a: "A Family plan is in beta. Contact us if you'd like an invite." },
];

export default function Pricing() {
  return (
    <div>
      <section className="py-20 lg:py-28" data-testid="pricing-hero">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="label-eyebrow mb-5">Pricing</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
            Free to start.
            <br />
            <span className="text-mint-600 dark:text-mint">Pro</span> when you outgrow it.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Your first vault is free, forever. Upgrade to Pro to add more vaults, the decoy vault, and biometric unlock.
          </p>
        </div>
      </section>

      <section className="pb-24" data-testid="pricing-tiers-section">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-6">
          {tiers.map((t) => (
            <div
              key={t.name}
              className={`relative glass-card p-8 lg:p-10 flex flex-col ${
                t.accent ? "ring-2 ring-mint shadow-2xl shadow-mint-500/20" : ""
              }`}
              data-testid={`pricing-tier-${t.name.toLowerCase()}`}
            >
              {t.accent && (
                <div className="absolute -top-3 left-8 pill !bg-mint !text-ink !border-mint-300">
                  <Crown className="h-3 w-3" />
                  Most popular
                </div>
              )}
              <div className="flex items-center gap-2">
                {t.accent ? (
                  <Sparkles className="h-4 w-4 text-mint-600 dark:text-mint" />
                ) : null}
                <h3 className="font-heading text-2xl font-bold">{t.name}</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{t.tagline}</p>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-heading text-5xl font-bold tracking-tighter">
                  {t.price}
                </span>
                <span className="text-sm text-muted-foreground">{t.period}</span>
              </div>
              <ul className="mt-8 space-y-3.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-mint flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-ink" strokeWidth={3} />
                    </span>
                    <span className="text-foreground/90">{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/download" className="mt-10" data-testid={`pricing-cta-${t.name.toLowerCase()}`}>
                <Button
                  className={`w-full rounded-full py-6 text-base ${
                    t.accent
                      ? "bg-mint text-ink hover:bg-mint-300 shadow-lg shadow-mint-500/30"
                      : "bg-foreground text-background hover:bg-foreground/90"
                  }`}
                >
                  {t.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-24" data-testid="pricing-faq-section">
        <div className="max-w-3xl mx-auto px-6">
          <SectionHeading title="Pricing FAQ" align="center" />
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {faqs.map((f, i) => (
              <div
                key={i}
                className="glass-card p-6"
                data-testid={`pricing-faq-${i}`}
              >
                <p className="font-heading font-semibold">{f.q}</p>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
